import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { PublicHeader, PublicFooter } from "@/components/PublicShell";
import AmountDialog from "@/components/AmountDialog";
import { toast } from "sonner";
import {
  Copy, Check, Heart, ShieldCheck, Users, Trophy,
  FacebookLogo, WhatsappLogo, XLogo, Envelope, PawPrint, X,
} from "@phosphor-icons/react";

function Progress({ raised, goal }) {
  const pct = Math.min(100, (raised / goal) * 100);
  return (
    <div className="w-full">
      <div className="flex items-baseline justify-between mb-2 gap-2">
        <div className="min-w-0">
          <span className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-[#1F2A1C]" data-testid="campaign-raised">
            {formatINR(raised)}
          </span>
          <span className="text-xs sm:text-sm text-[#56624F] ml-2">raised of {formatINR(goal)} goal</span>
        </div>
        <span className="text-sm font-semibold text-[#4FAC39] shrink-0" data-testid="campaign-percent">{pct.toFixed(2)}%</span>
      </div>
      <div className="h-3 rounded-full bg-[#DCE5D4] overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#4FAC39] to-[#7BC467] relative progress-shimmer" style={{ width: pct + "%" }} />
      </div>
    </div>
  );
}

function CopyButton({ value, testid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(value); setDone(true); toast.success("Copied"); setTimeout(() => setDone(false), 1800); }}
      className="inline-flex items-center gap-1 text-[#4FAC39] hover:text-[#3E8B2D] font-medium text-sm"
      data-testid={testid}
    >
      {done ? <Check size={16} /> : <Copy size={16} />} {done ? "Copied" : "Copy"}
    </button>
  );
}

function ImageCarousel({ images = [] }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (images.length < 2) return;
    const t = setInterval(() => setI((v) => (v + 1) % images.length), 5000);
    return () => clearInterval(t);
  }, [images.length]);
  if (!images.length) return null;
  return (
    <div className="space-y-3" data-testid="campaign-carousel">
      <div className="relative aspect-[4/3] md:aspect-[16/10] bg-[#EEF4E9] rounded-3xl overflow-hidden">
        {images.map((src, idx) => (
          <img key={idx} src={src} alt={"campaign " + (idx + 1)} className={"absolute inset-0 w-full h-full object-cover transition-opacity duration-700 " + (idx === i ? "opacity-100" : "opacity-0")} />
        ))}
        <div className="absolute top-4 left-4 bg-white/90 backdrop-blur rounded-full px-3 py-1.5 text-xs font-semibold text-[#2F7A4A] flex items-center gap-1.5 shadow-sm">
          <ShieldCheck size={14} weight="fill" /> 80G Tax Benefits
        </div>
      </div>
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {images.map((src, idx) => (
            <button key={idx} onClick={() => setI(idx)}
              className={"shrink-0 w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl overflow-hidden border-2 transition-all " + (idx === i ? "border-[#4FAC39] scale-[1.03]" : "border-[#DCE5D4] opacity-70")}
              data-testid={`carousel-thumb-${idx}`}>
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PublicCampaign() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAmount, setShowAmount] = useState(false);
  const [showExit, setShowExit] = useState(false);
  const [liveToast, setLiveToast] = useState(null);
  const [liveToastDismissed, setLiveToastDismissed] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [slides, setSlides] = useState([]);
  const [branding, setBranding] = useState({ instagram_handle: "seva.animals", exit_popup_image_url: "", exit_popup_title: "Please don't go yet", exit_popup_subtext: "" });

  useEffect(() => {
    if (!slug) return;
    api.get(`/campaigns/${slug}`).then((r) => setC(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/slides").then((r) => setSlides(r.data || [])).catch(() => {});
    api.get("/settings/public").then((r) => { if (r.data?.branding) setBranding(r.data.branding); }).catch(() => {});
  }, [slug]);

  // Live donor toast
  useEffect(() => {
    if (!c?.recent_donors?.length || liveToastDismissed) return;
    const pool = c.recent_donors.filter((d) => !d.anonymous && (d.donor_name || "").toLowerCase() !== "anonymous");
    if (!pool.length) return;
    let idx = 0;
    const show = () => {
      const d = pool[idx % pool.length];
      setLiveToast(d);
      idx++;
      setTimeout(() => setLiveToast((cur) => (cur === d ? null : cur)), 5500);
    };
    const t1 = setTimeout(show, 4000);
    const iv = setInterval(show, 22000);
    return () => { clearTimeout(t1); clearInterval(iv); };
  }, [c?.recent_donors, liveToastDismissed]);

  // Auto-open popup when ?donate=1 in URL
  useEffect(() => {
    if (!c) return;
    const p = new URLSearchParams(window.location.search);
    if (p.get("donate") === "1") setShowAmount(true);
  }, [c]);

  // Auto-open amount popup after 30 seconds on the page
  useEffect(() => {
    if (!c) return;
    if (sessionStorage.getItem("seva_auto_opened") === "1") return;
    const t = setTimeout(() => {
      sessionStorage.setItem("seva_auto_opened", "1");
      setShowAmount(true);
    }, 30000);
    return () => clearTimeout(t);
  }, [c]);

  if (loading) return <div className="min-h-screen grid place-items-center text-[#56624F]">Loading campaign...</div>;
  if (!c) return <div className="min-h-screen grid place-items-center">Campaign not found.</div>;

  const allImages = (slides.map((s) => s.image_url).filter(Boolean).concat(c.images || [])).slice(0, 10);

  const openDonate = () => { setShowExit(false); setShowAmount(true); };
  // When user tries to CLOSE the amount dialog -> show exit-intent
  const attemptCloseAmount = () => { setShowAmount(false); setShowExit(true); };
  const forceCloseAll = () => { setShowAmount(false); setShowExit(false); };

  return (
    <>
      <PublicHeader onDonateClick={openDonate} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pt-5 md:pt-12 pb-6">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-start">
          <div className="fade-up min-w-0">
            <ImageCarousel images={allImages.length ? allImages : c.images} />
          </div>
          <div className="fade-up space-y-5 md:space-y-6 min-w-0" style={{ animationDelay: "0.1s" }}>
            <div className="inline-flex items-center gap-2 bg-[#EEF4E9] px-3 py-1.5 rounded-full text-xs font-semibold text-[#2F7A4A] uppercase tracking-widest">
              <PawPrint size={14} weight="fill" /> {c.short_tag}
            </div>
            <h1 className="font-display font-bold text-2xl sm:text-3xl md:text-5xl leading-[1.1] break-words" data-testid="campaign-title">
              {c.title}
            </h1>
            <Progress raised={c.raised} goal={c.goal_amount} />
            <div className="grid grid-cols-3 gap-2 sm:gap-3 text-center">
              <div className="seva-card !p-3 sm:!p-4">
                <div className="font-display font-bold text-lg sm:text-xl text-[#1F2A1C]">{c.donor_count || 0}</div>
                <div className="text-[10px] sm:text-xs uppercase tracking-wider text-[#56624F]">Donors</div>
              </div>
              <div className="seva-card !p-3 sm:!p-4">
                <div className="font-display font-bold text-lg sm:text-xl text-[#2F7A4A]">100%</div>
                <div className="text-[10px] sm:text-xs uppercase tracking-wider text-[#56624F]">Goes to rescue</div>
              </div>
              <div className="seva-card !p-3 sm:!p-4">
                <div className="font-display font-bold text-lg sm:text-xl text-[#1F2A1C]">80G</div>
                <div className="text-[10px] sm:text-xs uppercase tracking-wider text-[#56624F]">Tax Benefit</div>
              </div>
            </div>
            <button onClick={openDonate} className="btn-primary w-full md:w-auto px-10 py-4 text-base md:text-lg inline-flex items-center justify-center gap-2" data-testid="hero-donate-btn">
              <Heart size={20} weight="fill" /> Donate Now
            </button>
            <div className="flex items-center gap-2 text-sm text-[#2F7A4A]">
              <ShieldCheck size={18} weight="fill" className="shrink-0" />
              <span className="text-xs sm:text-sm">100% of your donation goes directly towards rescue &amp; medical treatment.</span>
            </div>
          </div>
        </div>
      </section>

      {/* UPI / Bank */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-8">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="seva-card text-center">
            <h3 className="font-display font-bold text-xl mb-4">Or Donate using UPI</h3>
            <div className="flex flex-col items-center gap-4">
              {c.qr_image && <img src={c.qr_image} alt="UPI QR" className="w-48 h-48 rounded-xl border border-[#DCE5D4] object-cover bg-white" />}
              <div className="space-y-2 w-full max-w-sm">
                <div className="text-xs uppercase tracking-widest text-[#56624F]">UPI ID</div>
                <div className="flex items-center justify-between gap-2 bg-[#EEF4E9] rounded-xl px-3 py-2">
                  <code className="text-sm break-all min-w-0 text-left" data-testid="upi-id">{c.upi_id}</code>
                  <CopyButton value={c.upi_id} testid="copy-upi" />
                </div>
                <p className="text-xs text-[#56624F]">Works with all UPI apps: Google Pay, PhonePe, Paytm, BHIM, etc.</p>
              </div>
            </div>
          </div>
          <div className="seva-card">
            <h3 className="font-display font-bold text-xl mb-3">Donate via Bank Transfer</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2"><div className="min-w-0"><div className="text-xs uppercase tracking-widest text-[#56624F]">Account No.</div><div className="font-mono text-[#1F2A1C] break-all">{c.bank_account_no}</div></div><CopyButton value={c.bank_account_no} testid="copy-acc-no" /></div>
              <div className="flex items-start justify-between gap-2"><div className="min-w-0"><div className="text-xs uppercase tracking-widest text-[#56624F]">Account Name</div><div className="text-sm text-[#1F2A1C] break-words">{c.bank_account_name}</div></div><CopyButton value={c.bank_account_name} testid="copy-acc-name" /></div>
              <div className="flex items-center justify-between gap-2"><div className="min-w-0"><div className="text-xs uppercase tracking-widest text-[#56624F]">IFSC Code</div><div className="font-mono text-[#1F2A1C]">{c.bank_ifsc}</div></div><CopyButton value={c.bank_ifsc} testid="copy-ifsc" /></div>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section id="story" className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-10">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-4">Our Story</h2>
            <div
              className="text-[#2B3427] text-base md:text-lg leading-relaxed break-words story-rich"
              dangerouslySetInnerHTML={{ __html: /<\/?[a-z][\s\S]*?>/i.test(c.story || "") ? (c.story || "") : (c.story || "").replace(/\n/g, "<br/>") }}
            />
            {(c.story_images && c.story_images.length > 0) ? (
              <div className="grid sm:grid-cols-2 gap-4 mt-8">
                {c.story_images.map((url, i) => (
                  <img key={i} src={url} alt={"Our story " + (i + 1)} className="rounded-2xl w-full h-56 object-cover" data-testid={`story-image-${i}`} />
                ))}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4 mt-8">
                <img src="https://seva-india.org/wp-content/uploads/2025/10/1.jpg" alt="" className="rounded-2xl w-full h-56 object-cover" />
                <img src="https://seva-india.org/wp-content/uploads/2025/10/2.jpg" alt="" className="rounded-2xl w-full h-56 object-cover" />
              </div>
            )}
          </div>
          <aside className="space-y-6">
            <div className="seva-card">
              <h3 className="font-display font-bold text-lg flex items-center gap-2 mb-4"><Trophy size={20} weight="fill" className="text-[#4FAC39]" /> Top Donors</h3>
              <ul className="space-y-3">
                {(c.top_donors || []).slice(0, 5).map((d, i) => (
                  <li key={i} className="flex items-center justify-between gap-2" data-testid={`top-donor-${i}`}>
                    <div className="flex items-center gap-3 min-w-0"><div className="w-8 h-8 rounded-full bg-[#EEF4E9] grid place-items-center text-xs font-bold text-[#4FAC39] shrink-0">{i + 1}</div><span className="text-sm font-medium truncate">{d.donor_name}</span></div>
                    <span className="text-sm font-semibold shrink-0">{formatINR(d.amount)}</span>
                  </li>
                ))}
                {!c.top_donors?.length && <li className="text-sm text-[#56624F]">Be the first donor!</li>}
              </ul>
            </div>
            <div className="seva-card">
              <h3 className="font-display font-bold text-lg flex items-center gap-2 mb-4"><Users size={20} weight="fill" className="text-[#4FAC39]" /> Recent Donors</h3>
              <ul className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
                {(c.recent_donors || []).map((d, i) => (
                  <li key={i} className="flex items-center justify-between text-sm gap-2"><span className="truncate">{d.donor_name}</span><span className="font-semibold text-[#4FAC39] shrink-0">{formatINR(d.amount)}</span></li>
                ))}
                {!c.recent_donors?.length && <li className="text-sm text-[#56624F]">No donations yet. Be the first!</li>}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* Help breakdown */}
      <section className="bg-[#EEF4E9]/60 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10">
          <h2 className="font-display font-bold text-2xl md:text-4xl mb-10 text-center">How your contribution helps</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {(c.help_sections || []).map((sec, i) => (
              <div key={i} className="seva-card" data-testid={`help-section-${i}`}>
                <h3 className="font-display font-bold text-xl mb-4 text-[#4FAC39]">{sec.title}</h3>
                <ul className="space-y-2">
                  {sec.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm"><Check size={16} weight="bold" className="text-[#2F7A4A] mt-0.5 shrink-0" /><span>{item}</span></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Instagram CTA */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 md:px-10 py-8">
        <a href={`https://instagram.com/${branding.instagram_handle || "seva.animals"}`} target="_blank" rel="noopener noreferrer" className="seva-card flex items-center gap-4 hover:shadow-lg transition-shadow bg-gradient-to-br from-[#EEF7EA] to-white" data-testid="instagram-cta">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] grid place-items-center text-white shrink-0">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M12 2.2c3.2 0 3.6 0 4.8.1 1.2.1 1.9.2 2.3.4.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.2.4.3 1.1.4 2.3.1 1.3.1 1.7.1 4.8s0 3.5-.1 4.8c-.1 1.2-.2 1.9-.4 2.3-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.2-1.1.3-2.3.4-1.3.1-1.6.1-4.8.1s-3.5 0-4.8-.1c-1.2-.1-1.9-.2-2.3-.4a3.4 3.4 0 0 1-1.4-.9 3.4 3.4 0 0 1-.9-1.4c-.2-.4-.3-1.1-.4-2.3C2.2 15.6 2.2 15.2 2.2 12s0-3.5.1-4.8c.1-1.2.2-1.9.4-2.3.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1.1-.3 2.3-.4C8.5 2.2 8.8 2.2 12 2.2m0-2.2C8.7 0 8.3 0 7 .1 5.8.1 5 .3 4.2.6 3.4.9 2.7 1.3 2 2c-.7.7-1.1 1.4-1.4 2.2-.3.8-.5 1.6-.5 2.8C0 8.3 0 8.7 0 12s0 3.7.1 5c.1 1.2.3 2 .5 2.8.3.8.7 1.5 1.4 2.2.7.7 1.4 1.1 2.2 1.4.8.3 1.6.5 2.8.5C8.3 24 8.7 24 12 24s3.7 0 5-.1c1.2-.1 2-.3 2.8-.5.8-.3 1.5-.7 2.2-1.4.7-.7 1.1-1.4 1.4-2.2.3-.8.5-1.6.5-2.8.1-1.3.1-1.7.1-5s0-3.7-.1-5c-.1-1.2-.3-2-.5-2.8-.3-.8-.7-1.5-1.4-2.2A6.2 6.2 0 0 0 19.8.6C19 .3 18.2.1 17 .1 15.7 0 15.3 0 12 0Zm0 5.8A6.2 6.2 0 1 0 18.2 12 6.2 6.2 0 0 0 12 5.8Zm0 10.2A4 4 0 1 1 16 12a4 4 0 0 1-4 4Zm6.4-10.9a1.4 1.4 0 1 0 1.4 1.4 1.4 1.4 0 0 0-1.4-1.4Z"/></svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold">Follow us on Instagram for updates on our cases</div>
            <div className="text-sm text-[#56624F]">@{branding.instagram_handle || "seva.animals"}</div>
          </div>
          <div className="text-[#4FAC39] font-semibold text-sm hidden sm:block">Follow &rarr;</div>
        </a>
      </section>

      {/* Share */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-14 text-center">
        <h2 className="font-display font-bold text-2xl md:text-3xl mb-2">Every share can bring &#8377;3,000</h2>
        <p className="text-[#56624F] mb-6">Help us reach more kind hearts.</p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          {[
            { Icon: FacebookLogo, url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}` },
            { Icon: WhatsappLogo, url: `https://api.whatsapp.com/send/?text=${encodeURIComponent(c.title + " " + window.location.href)}` },
            { Icon: XLogo, url: `https://x.com/intent/tweet?text=${encodeURIComponent(c.title)}&url=${encodeURIComponent(window.location.href)}` },
            { Icon: Envelope, url: `mailto:?subject=Support ${encodeURIComponent(c.title)}&body=${encodeURIComponent(window.location.href)}` },
          ].map(({ Icon, url }, i) => (
            <a key={i} href={url} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-[#1F2A1C] text-white grid place-items-center hover:bg-[#4FAC39] transition-colors">
              <Icon size={20} weight="fill" />
            </a>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 md:px-10 pb-20">
        <h2 className="font-display font-bold text-2xl md:text-4xl mb-8 text-center">FAQs</h2>
        <div className="space-y-3">
          {(c.faqs || []).map((f, i) => (
            <div key={i} className="seva-card !p-0 overflow-hidden" data-testid={`faq-${i}`}>
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full text-left px-5 py-4 flex items-center justify-between gap-3 font-medium">
                <span className="text-left">{f.q}</span>
                <span className={"transition-transform shrink-0 " + (openFaq === i ? "rotate-45" : "")}>+</span>
              </button>
              {openFaq === i && <div className="px-5 pb-5 text-[#56624F] leading-relaxed">{f.a}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* Sticky mobile CTA */}
      <div className="fixed bottom-0 inset-x-0 md:hidden bg-white border-t border-[#DCE5D4] z-30 p-3 flex items-center gap-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-[#56624F]">Raised</div>
          <div className="font-display font-bold text-sm truncate">{formatINR(c.raised)} / {formatINR(c.goal_amount)}</div>
        </div>
        <button onClick={openDonate} className="btn-primary !py-3 !px-6 shrink-0" data-testid="mobile-sticky-donate">Donate</button>
      </div>

      {/* Live donor toast */}
      {liveToast && (
        <div className="fixed bottom-20 md:bottom-6 left-4 right-4 md:right-auto md:max-w-xs bg-white border border-[#DCE5D4] rounded-2xl shadow-xl p-3 pl-4 pr-2 flex items-center gap-3 toast-enter z-30" data-testid="live-donor-toast">
          <div className="w-9 h-9 rounded-full bg-[#EEF4E9] grid place-items-center shrink-0"><Heart size={18} weight="fill" className="text-[#4FAC39]" /></div>
          <div className="min-w-0 flex-1"><div className="text-sm font-semibold truncate">{liveToast.donor_name}</div><div className="text-xs text-[#56624F]">just donated {formatINR(liveToast.amount)}</div></div>
          <button onClick={() => { setLiveToast(null); setLiveToastDismissed(true); }} className="p-1.5 rounded-full hover:bg-[#EEF4E9] text-[#56624F] shrink-0" aria-label="Close" data-testid="live-toast-close"><X size={16} /></button>
        </div>
      )}

      {/* Exit intent modal (only triggered by closing amount dialog) */}
      {showExit && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setShowExit(false)} data-testid="exit-intent-modal">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 md:p-8 text-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={branding.exit_popup_image_url || "https://images.unsplash.com/photo-1537151625747-768eb6cf92b2?w=400&q=80"}
              alt="" className="w-36 h-36 object-contain mx-auto mb-5"
              data-testid="exit-popup-image"
            />
            <h3 className="font-display font-bold text-2xl mb-2" data-testid="exit-popup-title">{branding.exit_popup_title || "Please don't go yet"}</h3>
            <p className="text-[#56624F] mb-6 text-sm md:text-base" data-testid="exit-popup-subtext">{branding.exit_popup_subtext || "Your humble donations have the power to transform many lives positively and make a lasting impact."}</p>
            <div className="space-y-2">
              <button onClick={() => { setShowExit(false); setShowAmount(true); }} className="btn-primary w-full uppercase tracking-wide" data-testid="exit-yes-help">Yes, I will help</button>
              <button onClick={() => setShowExit(false)} className="btn-secondary w-full uppercase tracking-wide" data-testid="exit-not-today">Sorry, not today</button>
            </div>
          </div>
        </div>
      )}

      <AmountDialog open={showAmount} campaign={c} onClose={() => setShowAmount(false)} onAttemptClose={attemptCloseAmount} forceClose={forceCloseAll} />

      <PublicFooter />
    </>
  );
}
